import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';
import { PropertyOverlay } from '../components/PropertyOverlay';
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Pin,
  InfoWindow,
  type MapMouseEvent,
} from '@vis.gl/react-google-maps';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GOOGLE_MAPS_API_KEY = 'AIzaSyBNpVf2wxLdPMDj6_VAKG_KM179d7sV2vI';
const PROPERTY_CENTER = { lat: 44.866701, lng: -77.381202 };

const SITE_TYPES = [
  { id: 'tent_pad', label: 'Tent Pad' },
  { id: 'rv_pad', label: 'RV Pad' },
  { id: 'lean_to', label: 'Lean-to' },
  { id: 'hammock', label: 'Hammock' },
  { id: 'primitive', label: 'Primitive' },
  { id: 'other', label: 'Other' },
];

const CONDITIONS = [
  { id: 'excellent', label: 'Excellent' },
  { id: 'good', label: 'Good' },
  { id: 'fair', label: 'Fair' },
  { id: 'poor', label: 'Poor' },
  { id: 'needs_repair', label: 'Needs Repair' },
];

const FEATURES = [
  { id: 'fire_ring', label: 'Fire Ring' },
  { id: 'picnic_table', label: 'Picnic Table' },
  { id: 'water_hookup', label: 'Water Hookup' },
  { id: 'electrical_hookup', label: 'Electrical Hookup' },
  { id: 'level_pad', label: 'Level Pad' },
  { id: 'shade', label: 'Shade' },
  { id: 'waterfront', label: 'Waterfront' },
  { id: 'sheltered', label: 'Sheltered' },
  { id: 'privy_nearby', label: 'Privy Nearby' },
];

const TYPE_MAP = new window.Map(SITE_TYPES.map((t) => [t.id, t.label]));

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CampSite {
  id: string;
  name: string;
  type: string;
  capacity: number;
  description: string | null;
  features: string[];
  condition: string;
  locationLat: string | null;
  locationLon: string | null;
}

type PanelMode =
  | { type: 'none' }
  | { type: 'confirm'; lat: number; lng: number }
  | { type: 'create'; lat?: number; lng?: number }
  | { type: 'edit'; item: CampSite }
  | { type: 'view'; item: CampSite };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ConditionDot({ condition }: { condition: string }) {
  const colors: Record<string, string> = { excellent: '#28A745', good: '#28A745', fair: '#FFC107', poor: '#DC3545', needs_repair: '#DC3545' };
  return <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[condition] || '#888', flexShrink: 0 }} />;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CampSitesPage() {
  const [sites, setSites] = useState<CampSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState<PanelMode>({ type: 'none' });

  // Form state
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('tent_pad');
  const [formCapacity, setFormCapacity] = useState(2);
  const [formDescription, setFormDescription] = useState('');
  const [formCondition, setFormCondition] = useState('good');
  const [formFeatures, setFormFeatures] = useState<string[]>([]);
  const [formLat, setFormLat] = useState('');
  const [formLon, setFormLon] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const res = await apiFetch<CampSite[]>('/api/admin/camp-sites');
    if (res.success && res.data) setSites(res.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  function resetForm() {
    setFormName('');
    setFormType('tent_pad');
    setFormCapacity(2);
    setFormDescription('');
    setFormCondition('good');
    setFormFeatures([]);
    setFormLat('');
    setFormLon('');
  }

  // --- Map click: place pin ---
  const handleMapClick = useCallback((e: MapMouseEvent) => {
    if (panel.type !== 'none' && panel.type !== 'view') return;
    if (!e.detail.latLng) return;
    setPanel({ type: 'confirm', lat: e.detail.latLng.lat, lng: e.detail.latLng.lng });
  }, [panel.type]);

  // --- Confirm pin -> open create form ---
  function confirmPin() {
    if (panel.type !== 'confirm') return;
    resetForm();
    setFormLat(panel.lat.toFixed(7));
    setFormLon(panel.lng.toFixed(7));
    setPanel({ type: 'create', lat: panel.lat, lng: panel.lng });
  }

  // --- View item ---
  function viewItem(item: CampSite) {
    setPanel({ type: 'view', item });
  }

  // --- Edit item ---
  function openEdit(item: CampSite) {
    setFormName(item.name);
    setFormType(item.type);
    setFormCapacity(item.capacity);
    setFormDescription(item.description || '');
    setFormCondition(item.condition);
    setFormFeatures(item.features || []);
    setFormLat(item.locationLat || '');
    setFormLon(item.locationLon || '');
    setPanel({ type: 'edit', item });
  }

  // --- Save ---
  async function handleSave() {
    if (!formName.trim()) return;

    const payload = {
      name: formName.trim(),
      type: formType,
      capacity: formCapacity,
      description: formDescription || undefined,
      condition: formCondition,
      features: formFeatures,
      location_lat: formLat ? parseFloat(formLat) : undefined,
      location_lon: formLon ? parseFloat(formLon) : undefined,
    };

    if (panel.type === 'edit') {
      await apiFetch(`/api/admin/camp-sites/${panel.item.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    } else {
      await apiFetch('/api/admin/camp-sites', { method: 'POST', body: JSON.stringify(payload) });
    }
    setPanel({ type: 'none' });
    fetchData();
  }

  // --- Delete ---
  async function handleDelete(item: CampSite) {
    if (!confirm(`Delete "${item.name}"?`)) return;
    await apiFetch(`/api/admin/camp-sites/${item.id}`, { method: 'DELETE' });
    setPanel({ type: 'none' });
    fetchData();
  }

  function toggleFeature(f: string) {
    setFormFeatures((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]);
  }

  const mappedSites = sites.filter((s) => s.locationLat && s.locationLon);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div style={s.page}>
      {/* LEFT: Map */}
      <div style={s.mapCol}>
        <div style={s.mapHeader}>
          <h1 style={s.pageTitle}>Camp Sites</h1>
          {panel.type === 'none' && (
            <span style={s.mapHint}>Click the map to place a new camp site</span>
          )}
        </div>
        <div style={{ ...s.mapWrap, position: 'relative' }}>
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <GoogleMap
              defaultCenter={PROPERTY_CENTER}
              defaultZoom={15}
              mapTypeId="satellite"
              gestureHandling="greedy"
              fullscreenControl={false}
              mapId="grimsthorpe-admin-campsites"
              onClick={handleMapClick}
              style={{ width: '100%', height: '100%' }}
            >
              <PropertyOverlay />

              {/* Existing camp site markers */}
              {mappedSites.map((site) => {
                const isEditing = panel.type === 'edit' && panel.item.id === site.id;
                const lat = isEditing && formLat ? parseFloat(formLat) : parseFloat(site.locationLat!);
                const lng = isEditing && formLon ? parseFloat(formLon) : parseFloat(site.locationLon!);
                return (
                  <AdvancedMarker
                    key={site.id}
                    position={{ lat, lng }}
                    title={site.name}
                    draggable={isEditing}
                    onClick={() => { if (!isEditing) viewItem(site); }}
                    onDragEnd={(e) => {
                      if (isEditing && e.latLng) {
                        setFormLat(e.latLng.lat().toFixed(7));
                        setFormLon(e.latLng.lng().toFixed(7));
                      }
                    }}
                  >
                    <Pin
                      background="#8B4513"
                      borderColor={isEditing ? '#00BFFF' : '#000'}
                      glyphColor="#FFF"
                      scale={0.8}
                    />
                  </AdvancedMarker>
                );
              })}

              {/* Pending pin */}
              {panel.type === 'confirm' && (
                <AdvancedMarker
                  position={{ lat: panel.lat, lng: panel.lng }}
                  draggable
                  onDragEnd={(e) => { if (e.latLng) setPanel({ type: 'confirm', lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
                >
                  <Pin background="#FFC107" borderColor="#FF8F00" glyphColor="#000" />
                </AdvancedMarker>
              )}

              {/* Info window */}
              {panel.type === 'view' && panel.item.locationLat && (
                <InfoWindow
                  position={{ lat: parseFloat(panel.item.locationLat!), lng: parseFloat(panel.item.locationLon!) }}
                  onCloseClick={() => setPanel({ type: 'none' })}
                  pixelOffset={[0, -40]}
                >
                  <div style={{ color: '#000', minWidth: 160 }}>
                    <strong style={{ fontSize: 14 }}>{panel.item.name}</strong>
                    <p style={{ margin: '4px 0', fontSize: 11 }}>
                      {TYPE_MAP.get(panel.item.type)} &middot; {panel.item.condition?.replace('_', ' ')} &middot; {panel.item.capacity} cap
                    </p>
                    <button onClick={() => openEdit(panel.item)}
                      style={{ marginTop: 4, padding: '4px 12px', fontSize: 12, backgroundColor: '#1B3B1B', color: '#FFD700', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                      Edit Camp Site
                    </button>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </APIProvider>
        </div>
      </div>

      {/* RIGHT: Panel */}
      <div style={s.panelCol}>
        <div style={s.panelContent}>
          {/* Default: site list */}
          {panel.type === 'none' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={s.panelTitle}>Sites</h3>
                <button style={s.btnPrimary} onClick={() => { resetForm(); setPanel({ type: 'create' }); }}>+ Add</button>
              </div>

              <p style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 12 }}>
                {sites.length} site{sites.length !== 1 ? 's' : ''} &middot; {mappedSites.length} on map. Click map to place or click an item below.
              </p>

              {sites.map((site) => (
                <div key={site.id} style={s.itemRow} onClick={() => site.locationLat ? viewItem(site) : openEdit(site)}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ color: COLORS.textPrimary, fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{site.name}</div>
                    <div style={{ color: COLORS.textMuted, fontSize: 10 }}>
                      {TYPE_MAP.get(site.type)} &middot; {site.capacity} cap &middot; {site.condition?.replace('_', ' ')}
                      {site.locationLat ? '' : ' · no location'}
                    </div>
                  </div>
                  <ConditionDot condition={site.condition} />
                </div>
              ))}

              {sites.length === 0 && !loading && (
                <p style={{ color: COLORS.textMuted, fontSize: 13, fontStyle: 'italic' }}>No camp sites yet. Click the map or "+ Add" to get started.</p>
              )}
            </>
          )}

          {/* Confirm pin */}
          {panel.type === 'confirm' && (
            <>
              <h3 style={s.panelTitle}>Confirm Location</h3>
              <p style={{ color: COLORS.textMuted, fontSize: 13, marginBottom: 12 }}>Drag the yellow pin to adjust.</p>
              <p style={{ color: COLORS.textSecondary, fontSize: 12, fontFamily: 'monospace' }}>{panel.lat.toFixed(6)}, {panel.lng.toFixed(6)}</p>
              <div style={s.panelActions}>
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Cancel</button>
                <button style={s.btnPrimary} onClick={confirmPin}>Confirm & Create</button>
              </div>
            </>
          )}

          {/* View item */}
          {panel.type === 'view' && (
            <>
              <h3 style={s.panelTitle}>{panel.item.name}</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
                <span style={s.badge}>{TYPE_MAP.get(panel.item.type)}</span>
                <span style={s.badge}>{panel.item.capacity} capacity</span>
                <span style={s.badge}>{panel.item.condition.replace('_', ' ')}</span>
              </div>
              {panel.item.description && <p style={{ color: COLORS.textSecondary, fontSize: 13 }}>{panel.item.description}</p>}
              {panel.item.features && panel.item.features.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                  {panel.item.features.map((f) => (
                    <span key={f} style={s.featureBadge}>{f.replace(/_/g, ' ')}</span>
                  ))}
                </div>
              )}
              <div style={s.panelActions}>
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Close</button>
                <button style={s.btnPrimary} onClick={() => openEdit(panel.item)}>Edit</button>
              </div>
            </>
          )}

          {/* Create / Edit form */}
          {(panel.type === 'create' || panel.type === 'edit') && (
            <>
              <h3 style={s.panelTitle}>{panel.type === 'edit' ? 'Edit Camp Site' : 'New Camp Site'}</h3>

              <div style={s.field}>
                <label style={s.label}>Name</label>
                <input value={formName} onChange={(e) => setFormName(e.target.value)} style={s.input} autoFocus placeholder="e.g. Lakeside Pad 1" />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...s.field, flex: 2 }}>
                  <label style={s.label}>Type</label>
                  <select value={formType} onChange={(e) => setFormType(e.target.value)} style={s.input}>
                    {SITE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Capacity</label>
                  <input type="number" min={1} value={formCapacity} onChange={(e) => setFormCapacity(parseInt(e.target.value) || 1)} style={s.input} />
                </div>
              </div>

              <div style={s.field}>
                <label style={s.label}>Description</label>
                <textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} rows={2} style={{ ...s.input, resize: 'vertical' }} />
              </div>

              <div style={s.field}>
                <label style={s.label}>Condition</label>
                <select value={formCondition} onChange={(e) => setFormCondition(e.target.value)} style={s.input}>
                  {CONDITIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>

              <div style={s.field}>
                <label style={s.label}>Features</label>
                <div style={s.checkGrid}>
                  {FEATURES.map((f) => (
                    <label key={f.id} style={s.checkItem}>
                      <input type="checkbox" checked={formFeatures.includes(f.id)} onChange={() => toggleFeature(f.id)} style={{ accentColor: COLORS.primaryGreen }} />
                      <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>{f.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={s.panelActions}>
                {panel.type === 'edit' && (
                  <button style={{ ...s.btnDanger, marginRight: 'auto' }} onClick={() => handleDelete(panel.item)}>Delete</button>
                )}
                <button style={s.btnSecondary} onClick={() => setPanel({ type: 'none' })}>Cancel</button>
                <button style={s.btnPrimary} onClick={handleSave} disabled={!formName.trim()}>
                  {panel.type === 'edit' ? 'Update' : 'Create'}
                </button>
              </div>
            </>
          )}

          {loading && <div style={{ color: COLORS.textMuted, padding: 24 }}>Loading...</div>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  page: { display: 'flex', height: 'calc(100vh - 60px)', gap: 0 },
  mapCol: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  mapHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', flexShrink: 0 },
  pageTitle: { fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, margin: 0 },
  mapHint: { fontSize: 12, color: COLORS.textMuted, fontStyle: 'italic' },
  mapWrap: { flex: 1, borderRadius: 8, overflow: 'hidden', margin: '0 0 0 16px' },
  panelCol: { width: 380, flexShrink: 0, overflowY: 'auto', borderLeft: `1px solid ${COLORS.borderDefault}`, backgroundColor: COLORS.backgroundSecondary },
  panelContent: { padding: 20 },
  panelTitle: { fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: '0 0 16px' },
  panelActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { padding: '7px 10px', backgroundColor: COLORS.backgroundTertiary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, color: COLORS.textPrimary, fontSize: 13 },
  checkGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 },
  checkItem: { display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, backgroundColor: 'rgba(184,151,94,0.15)', color: COLORS.textGold },
  featureBadge: { display: 'inline-block', padding: '2px 8px', backgroundColor: 'rgba(139,69,19,0.15)', color: '#CD853F', borderRadius: 4, fontSize: 11, textTransform: 'capitalize' },
  itemRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', marginTop: 2, borderRadius: 6, cursor: 'pointer', border: `1px solid transparent` },
  btnPrimary: { padding: '7px 14px', backgroundColor: COLORS.primaryGreen, color: COLORS.textGold, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '7px 14px', backgroundColor: COLORS.backgroundTertiary, color: COLORS.textSecondary, border: `1px solid ${COLORS.borderDefault}`, borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnDanger: { padding: '7px 14px', backgroundColor: 'transparent', color: COLORS.statusError, border: '1px solid rgba(220,53,69,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
};
